
import React, { ReactNode } from "react";
import { z } from "zod";
import { useForm, UseFormProps } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage
} from "@/components/ui/form";
import { Button } from "@/components/ui/button";

type TierQuestionsProps<T extends z.ZodType> = {
  title: string;
  description: string;
  schema: T;
  defaultValues: z.infer<T>;
  children: (form: ReturnType<typeof useForm<z.infer<T>>>) => ReactNode;
  onSubmit: (data: z.infer<T>) => void;
  onBack: () => void;
  isSaving: boolean;
};

export default function TierQuestions<T extends z.ZodType>({
  title,
  description,
  schema,
  defaultValues,
  children,
  onSubmit,
  onBack,
  isSaving
}: TierQuestionsProps<T>) {
  const form = useForm<z.infer<T>>({
    resolver: zodResolver(schema),
    defaultValues,
  });

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
        <h3 className="text-white font-medium text-xl pb-2 border-b border-white/20">{title}</h3>
        <p className="text-white/70 text-sm mb-4">
          {description}
        </p>
        
        {children(form)}

        <div className="pt-4 flex justify-between">
          <Button 
            type="button" 
            variant="outline" 
            onClick={onBack}
            className="bg-white/10 text-white hover:bg-white/20 border-white/30"
          >
            Back
          </Button>
          <Button 
            type="submit" 
            variant="purple" 
            disabled={isSaving}
          >
            {isSaving ? (
              <>
                <span className="animate-spin mr-2">◌</span>
                Saving...
              </>
            ) : "Continue to Preferences"}
          </Button>
        </div>
      </form>
    </Form>
  );
}
